import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Target,
  Eye,
  Heart,
  Users,
  TreeStructure,
  Handshake,
  CheckCircle,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const coreValues = [
  {
    icon: Heart,
    title: "Community Ownership",
    description: "We believe communities know their needs best. Our approach centers on local leadership, participation, and ownership of development initiatives.",
  },
  {
    icon: Users,
    title: "Inclusive Growth",
    description: "We are committed to ensuring that the benefits of development reach everyone, especially women, youth, and marginalized groups.",
  },
  {
    icon: TreeStructure,
    title: "Sustainability",
    description: "Our programs are designed to create lasting impact that continues long after our direct involvement ends.",
  },
  {
    icon: Handshake,
    title: "Partnership",
    description: "We work collaboratively with communities, governments, NGOs, and the private sector to maximize our collective impact.",
  },
];

const teamMembers = [
  {
    name: "Executive Director",
    role: "Leadership",
    description: "Leading PEDA's vision and strategy for sustainable community development across Africa.",
    photo: null, // Placeholder for actual photo
  },
  {
    name: "Programs Director",
    role: "Program Management",
    description: "Overseeing all PEDA programs including TVET, enterprise development, and community empowerment initiatives.",
    photo: null,
  },
  {
    name: "Research & M&E Lead",
    role: "Research & Evaluation",
    description: "Ensuring evidence-based programming and measuring impact across all PEDA interventions.",
    photo: null,
  },
  {
    name: "Finance & Admin Manager",
    role: "Operations",
    description: "Managing financial operations and administrative functions to ensure organizational efficiency.",
    photo: null,
  },
  {
    name: "Training Coordinator",
    role: "Capacity Building",
    description: "Coordinating training programs and ensuring quality delivery of all educational interventions.",
    photo: null,
  },
  {
    name: "Community Liaison Officer",
    role: "Community Relations",
    description: "Building and maintaining relationships with communities and local stakeholders.",
    photo: null,
  },
];

export default function AboutPage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-slate-50" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                About PEDA AFRICA
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-6">
                A Research-Driven Organization Empowering Communities Through Enterprise & Innovation
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                PEDA AFRICA aims to create a sustainable future generation free from poverty by fostering entrepreneurship, driving innovation, and building strong, resilient communities through the power of community-led enterprises.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We are dedicated to fostering sustainable socio-economic development in Africa by empowering communities through a multi-sectoral approach that prioritizes community-led enterprises as key drivers of change.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png" 
                alt="PEDA Community"
                className="w-full h-[450px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-green-700 text-white rounded-2xl p-6 max-w-xs shadow-xl">
                <p className="font-semibold text-lg mb-2">Our Tagline</p>
                <p className="text-green-100 text-sm leading-relaxed">
                  "Enterprise. Skills. Community Transformation."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-28" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Purpose
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Vision & Mission
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-700 to-green-800 rounded-2xl p-8 md:p-10 text-white"
            >
              <div className="w-16 h-16 bg-amber-500/20 rounded-xl flex items-center justify-center mb-6">
                <Eye className="h-8 w-8 text-amber-400" weight="duotone" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-green-100 leading-relaxed text-lg">
                Africa where vibrant entrepreneurial ecosystems ignite inclusive growth, enabling communities to flourish and achieve their full potential.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-sm"
            >
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-green-700" weight="duotone" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                To build resilient, dependable, transformed, and innovative communities with a talent to nurture entrepreneurship development and contribute to economic prosperity for all.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="core-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              What Drives Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600">
              These principles guide everything we do and how we work with communities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="h-6 w-6 text-green-700" weight="duotone" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 md:py-28" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                Our Approach
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Community-Centered Development That Works
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We believe that sustainable development happens when communities lead their own transformation. Our approach combines rigorous research, practical training, and ongoing support to ensure lasting impact.
              </p>

              <div className="space-y-4">
                {[
                  "Research-driven interventions based on local needs assessment",
                  "Capacity building through practical, hands-on training",
                  "Enterprise development support from ideation to scale",
                  "Leadership development to sustain community transformation",
                  "Technology integration to enhance productivity and reach",
                  "Partnerships with local and international stakeholders",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-700 shrink-0 mt-0.5" weight="fill" />
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/e0f592gf_11.jpg" 
                alt="Agricultural Training"
                className="w-full h-48 object-cover rounded-2xl"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/lxiebqhu_55d8f7e2-761d-4c63-9839-a0652352eb5a.jpeg" 
                alt="Youth Exhibition"
                className="w-full h-48 object-cover rounded-2xl mt-8"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/v6qw6xmc_ph_28265_101967.jpg" 
                alt="School Agriculture"
                className="w-full h-48 object-cover rounded-2xl"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/zbcr8wiq_PicFrame3.jpg" 
                alt="Students"
                className="w-full h-48 object-cover rounded-2xl mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Team
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Meet the People Behind PEDA
            </h2>
            <p className="text-lg text-slate-600">
              Our dedicated team brings together expertise in community development, enterprise support, research, and program management.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg transition-all"
              >
                {/* Photo Placeholder */}
                <div className="h-48 bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                  {member.photo ? (
                    <img src={member.photo} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-24 h-24 bg-green-200 rounded-full flex items-center justify-center">
                      <Users className="h-12 w-12 text-green-700" weight="duotone" />
                    </div>
                  )}
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{member.name}</h3>
                  <p className="text-amber-600 font-medium text-sm mb-3">{member.role}</p>
                  <p className="text-slate-600 text-sm leading-relaxed">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8 text-sm">
            * Team photos and names can be updated with actual staff information
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Us in Building a Prosperous Africa
          </h2>
          <p className="text-lg text-green-100/80 mb-8 leading-relaxed">
            Whether you're a community group, development partner, government agency, or individual supporter, there are many ways to work with PEDA to create lasting change.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="about-contact-button"
            >
              <Link to="/contact">
                Get In Touch <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl px-8"
              data-testid="about-services-button"
            >
              <Link to="/services">Explore Our Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
